import { Skeleton } from "@mui/material";

import { ErrorTypography } from "components/ErrorTypography/ErrorTypography";
import { useSwrFetch } from "hooks/useSwrFetch";

import { BookedPriceDisplay } from "./BookedPriceDisplay";
import { BookedPriceRevealTier } from "./BookedPriceRevealTier";
import { BookedPriceRevealUnavailable } from "./BookedPriceRevealUnavailable";
import {
  COMPETITIVE_THRESHOLD,
  CompetitivenessTier,
  HIGHLY_COMPETITIVE_THRESHOLD,
  IN_THE_BALLPARK_THRESHOLD,
} from "./tiers";
import { getUnavailableMessage } from "./unavailableMessages";

interface BookedPriceRevealResultProps {
  shipmentId: number;
}

function mapToTier(percentage: number): CompetitivenessTier {
  if (percentage <= HIGHLY_COMPETITIVE_THRESHOLD) {
    return CompetitivenessTier.HIGHLY_COMPETITIVE;
  }
  if (percentage <= COMPETITIVE_THRESHOLD) {
    return CompetitivenessTier.COMPETITIVE;
  }
  if (percentage <= IN_THE_BALLPARK_THRESHOLD) {
    return CompetitivenessTier.IN_THE_BALLPARK;
  }
  return CompetitivenessTier.NOT_COMPETITIVE;
}

export function BookedPriceRevealResult(props: BookedPriceRevealResultProps) {
  const { shipmentId } = props;

  const { data, loading, error } = useSwrFetch<BookedShipmentPrice>({
    url: `/shipments/${shipmentId}/booked-price`,
    errorMessage: "Failed to load the booked price comparison",
  });

  if (loading) {
    return <Skeleton variant="rounded" width="100%" height={80} />;
  }

  if (error) {
    return (
      <ErrorTypography error="Unable to compare your quote to the booked price" />
    );
  }

  if (!data?.eligible) {
    return (
      <BookedPriceRevealUnavailable
        message={getUnavailableMessage(data?.reason)}
      />
    );
  }

  const tier = mapToTier(data.absolute_distance_percentage);

  return (
    <>
      {data.booked_price_disclosed && (
        <BookedPriceDisplay
          price={data.price}
          currencyCode={data.currency.code}
        />
      )}
      <BookedPriceRevealTier tier={tier} />
    </>
  );
}
